import { useEffect, useState } from 'react'
import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router-dom'
import {
  KITCHEN_ROUTE_ROLES,
  MANAGER_ROLES,
  OPERATIONS_ROLES,
  WAITER_ROUTE_ROLES,
} from './lib/routeRoles.js'
import { isAuthenticated, getTokenPayload, hasAnyRole, onTokenChange } from './lib/auth.js'
import { refreshSession } from './lib/api.js'
import { ensureTenantSession, resolveHomePath, tenantGateSatisfied } from './lib/session.js'
import type { TenantGate } from './lib/session.js'
import { Spinner } from './components/ui/index.js'
import { Shell } from './components/layout/Shell.js'
import { Login } from './pages/Login.js'
import { Home } from './pages/Home.js'
import { Dashboard } from './pages/Dashboard.js'
import { Branding } from './pages/Branding.js'
import { CountryCurrency } from './pages/CountryCurrency.js'
import { Translations } from './pages/Translations.js'
import { Menu } from './pages/Menu.js'
import { ReviewAttributes } from './pages/ReviewAttributes.js'
import { Combos } from './pages/Combos.js'
import { TaxRules } from './pages/TaxRules.js'
import { Tables } from './pages/Tables.js'
import { Kitchens } from './pages/Kitchens.js'
import { RestaurantUsers } from './pages/RestaurantUsers.js'
import { PlatformUsers } from './pages/PlatformUsers.js'
import { PlatformTranslations } from './pages/PlatformTranslations.js'
import { PlatformIdeas } from './pages/PlatformIdeas.js'
import { PlatformContacts } from './pages/PlatformContacts.js'
import { PlatformPlans } from './pages/PlatformPlans.js'
import { PlatformCountries } from './pages/PlatformCountries.js'
import { PlatformLeads } from './pages/PlatformLeads.js'
import { ImportMenu } from './pages/ImportMenu.js'
import { Kitchen } from './pages/Kitchen.js'
import { Waiter } from './pages/Waiter.js'
import { AdjustmentApprovals } from './pages/AdjustmentApprovals.js'
import { Payments } from './pages/Payments.js'
import { Location } from './pages/Location.js'

/**
 * Sessão do guard (hotfix #288): access expirado NÃO manda direto ao /login —
 * tenta o refresh (single-flight + lock entre abas) e só redireciona se ele
 * falhar. Reage a mudanças do token em outras abas (evento storage).
 */
export function useSession(): 'checking' | 'ok' | 'none' {
  const [state, setState] = useState<'checking' | 'ok' | 'none'>(() =>
    isAuthenticated() ? 'ok' : 'checking',
  )
  useEffect(() => {
    let alive = true
    const check = () => {
      if (isAuthenticated()) {
        setState('ok')
        return
      }
      setState('checking')
      void refreshSession().then((ok) => {
        if (alive) setState(ok && isAuthenticated() ? 'ok' : 'none')
      })
    }
    if (!isAuthenticated()) check()
    const off = onTokenChange(check)
    return () => {
      alive = false
      off()
    }
  }, [])
  return state
}

function RequireAuth() {
  const session = useSession()
  const { pathname } = useLocation()
  const pathSlug = pathname.match(/^\/r\/([^/]+)/)?.[1] ?? null
  // #319: em /r/:slug o painel só renderiza com um token DESTE tenant (inclui
  // o handoff do /contratar com o painel já aberto noutro restaurante, QA 3b):
  // antes, o token de outro tenant ia com o X-Tenant-Slug da URL → 403 em tudo.
  const [gate, setGate] = useState<{ slug: string; result: TenantGate } | null>(null)
  const gateReady =
    !pathSlug ||
    (gate?.slug === pathSlug ? gate.result.kind === 'ok' : tenantGateSatisfied(pathSlug))
  useEffect(() => {
    if (session !== 'ok' || !pathSlug) return
    if (tenantGateSatisfied(pathSlug)) {
      setGate({ slug: pathSlug, result: { kind: 'ok' } })
      return
    }
    let alive = true
    void ensureTenantSession(pathSlug).then((result) => {
      if (alive) setGate({ slug: pathSlug, result })
    })
    return () => {
      alive = false
    }
  }, [session, pathSlug])
  if (session === 'none') return <Navigate to="/login" replace />
  if (pathSlug && gate?.slug === pathSlug && gate.result.kind === 'redirect') {
    return <Navigate to={gate.result.to} replace />
  }
  if (session === 'checking' || !gateReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }
  return (
    <Shell>
      <Outlet />
    </Shell>
  )
}

/** Guarda de rota por role — kitchen/waiter só acessam suas telas. */
function RequireRole({ roles, children }: { roles: string[]; children: React.ReactElement }) {
  if (!hasAnyRole(getTokenPayload(), roles)) {
    return <Navigate to="/" replace />
  }
  return children
}

function DefaultRedirect() {
  // Espera a sessão resolver (refresh) antes de decidir — evita /login falso.
  const session = useSession()
  // #319: destino pelo tenant da SESSÃO (/auth/session), não pelo slug
  // guardado no localStorage (que pode ser de um tenant apagado)
  const [target, setTarget] = useState<string | null | undefined>(undefined)
  useEffect(() => {
    if (session !== 'ok') return
    let alive = true
    void resolveHomePath().then((path) => {
      if (alive) setTarget(path)
    })
    return () => {
      alive = false
    }
  }, [session])
  if (session === 'none') return <Navigate to="/login" replace />
  if (session === 'checking' || target === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }
  return <Navigate to={target ?? '/login'} replace />
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    element: <RequireAuth />,
    children: [
      { path: '/', element: <Home /> },
      {
        path: '/r/:slug/dashboard',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Dashboard />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/branding',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Branding />
          </RequireRole>
        ),
      },
      {
        // #486 item 1: o dono muda o país e a moeda do restaurante sozinho
        path: '/r/:slug/country-currency',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <CountryCurrency />
          </RequireRole>
        ),
      },
      {
        // #216: fila única de revisão — alergénios + dietas + picância + tags
        path: '/r/:slug/review-attributes',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <ReviewAttributes />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/menu',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Menu />
          </RequireRole>
        ),
      },
      {
        // #336: traduções do menu — página própria (antes vivia no Branding)
        path: '/r/:slug/translations',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Translations />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/combos',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Combos />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/tax-rules',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <TaxRules />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/tables',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Tables />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/users',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <RestaurantUsers />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/kitchens',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Kitchens />
          </RequireRole>
        ),
      },
      {
        // #229: Settings → Localização (pin confirmável + auditoria)
        path: '/r/:slug/location',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Location />
          </RequireRole>
        ),
      },
      {
        // #160: onboarding Stripe Connect do restaurante (pagamento online)
        path: '/r/:slug/payments',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <Payments />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/kitchen',
        element: (
          <RequireRole roles={KITCHEN_ROUTE_ROLES}>
            <Kitchen />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/import',
        element: (
          <RequireRole roles={MANAGER_ROLES}>
            <ImportMenu />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/waiter',
        element: (
          <RequireRole roles={WAITER_ROUTE_ROLES}>
            <Waiter />
          </RequireRole>
        ),
      },
      {
        path: '/r/:slug/adjustments',
        element: (
          <RequireRole roles={OPERATIONS_ROLES}>
            <AdjustmentApprovals />
          </RequireRole>
        ),
      },
      {
        path: '/platform-users',
        element: (
          <RequireRole roles={['platform_owner']}>
            <PlatformUsers />
          </RequireRole>
        ),
      },
      {
        path: '/platform-translations',
        element: (
          <RequireRole roles={['platform_owner']}>
            <PlatformTranslations />
          </RequireRole>
        ),
      },
      {
        path: '/platform-ideas',
        element: (
          <RequireRole roles={['platform_owner']}>
            <PlatformIdeas />
          </RequireRole>
        ),
      },
      {
        path: '/platform-plans',
        element: (
          <RequireRole roles={['platform_owner']}>
            <PlatformPlans />
          </RequireRole>
        ),
      },
      {
        path: '/platform-countries',
        element: (
          <RequireRole roles={['platform_owner']}>
            <PlatformCountries />
          </RequireRole>
        ),
      },
      {
        path: '/platform-leads',
        element: (
          <RequireRole roles={['platform_owner']}>
            <PlatformLeads />
          </RequireRole>
        ),
      },
      {
        path: '/platform-contacts',
        element: (
          <RequireRole roles={['platform_owner']}>
            <PlatformContacts />
          </RequireRole>
        ),
      },
    ],
  },
  { path: '*', element: <DefaultRedirect /> },
])
